import React, { useState } from 'react';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { 
  Container, 
  Typography, 
  TextField, 
  Button, 
  Box, 
  Grid, 
  Link, 
  Paper,
  Alert,
  InputAdornment,
  IconButton,
  Stepper,
  Step,
  StepLabel
} from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import { registerUser } from '../../services/authService';

const RegisterPage = () => {
  const navigate = useNavigate();
  
  // Estados para el formulario
  const [activeStep, setActiveStep] = useState(0);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  
  // Pasos del registro
  const steps = ['Información de cuenta', 'Datos personales'];
  
  // Validar el formulario actual
  const validateCurrentStep = () => {
    if (activeStep === 0) {
      // Validar email y contraseña
      if (!email) {
        setError('El correo electrónico es obligatorio');
        return false;
      }
      
      if (!password) {
        setError('La contraseña es obligatoria');
        return false;
      }
      
      if (password.length < 6) {
        setError('La contraseña debe tener al menos 6 caracteres');
        return false;
      }
      
      if (password !== confirmPassword) {
        setError('Las contraseñas no coinciden');
        return false;
      }
    } else if (activeStep === 1) {
      // Validar nombre y teléfono
      if (!name) {
        setError('El nombre es obligatorio');
        return false;
      }
      
      if (!phone) {
        setError('El teléfono es obligatorio');
        return false;
      }
    }
    
    return true;
  };
  
  // Avanzar al siguiente paso
  const handleNext = (e) => {
    e.preventDefault();
    setError('');
    
    if (validateCurrentStep()) {
      setActiveStep((prevStep) => prevStep + 1);
    }
  };
  
  // Retroceder al paso anterior
  const handleBack = () => {
    setActiveStep((prevStep) => prevStep - 1);
    setError('');
  };
  
  // Manejar envío del formulario
  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    
    if (!validateCurrentStep()) {
      return;
    }
    
    setLoading(true);
    
    try {
      // Registrar usuario con los datos del formulario
      const user = await registerUser(email, password, name, phone);
      console.log('Usuario registrado correctamente:', user);
      setLoading(false);
      
      // Redirigir a la página de cargas solo si todo fue exitoso
      navigate('/loads');
    } catch (error) {
      console.error('Error al registrar usuario:', error);
      
      setLoading(false);
      
      // Mostrar mensaje de error apropiado según el tipo de error
      if (error.code === 'auth/email-already-in-use') {
        setError('Este correo electrónico ya está en uso');
      } else if (error.code === 'auth/invalid-email') {
        setError('Correo electrónico inválido');
      } else if (error.code === 'auth/weak-password') {
        setError('La contraseña es demasiado débil');
      } else if (error.code === 'auth/network-request-failed') {
        setError('Error de conexión. Verifica tu conexión a internet.');
      } else {
        setError('Error al registrar usuario. Por favor, inténtalo de nuevo.');
      }
    }
  };
  
  // Alternar visibilidad de la contraseña
  const handleTogglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };
  
  return (
    <Container component="main" maxWidth="sm">
      <Paper elevation={3} sx={{ p: 4, mt: 8, borderRadius: 2 }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          <Typography component="h1" variant="h5" gutterBottom>
            Crear cuenta
          </Typography>
          
          <Stepper activeStep={activeStep} sx={{ width: '100%', mb: 4 }}>
            {steps.map((label) => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>
          
          {error && (
            <Alert severity="error" sx={{ width: '100%', mb: 2 }}>
              {error}
            </Alert>
          )}
          
          <Box component="form" onSubmit={activeStep === steps.length - 1 ? handleSubmit : handleNext} sx={{ mt: 1, width: '100%' }}>
            {activeStep === 0 ? (
              // Paso 1: Información de cuenta
              <>
                <TextField
                  margin="normal"
                  required
                  fullWidth
                  id="email"
                  label="Correo electrónico"
                  name="email"
                  autoComplete="email"
                  autoFocus
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  disabled={loading}
                />
                
                <TextField
                  margin="normal"
                  required
                  fullWidth
                  name="password"
                  label="Contraseña"
                  type={showPassword ? 'text' : 'password'}
                  id="password"
                  autoComplete="new-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  disabled={loading}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          aria-label="toggle password visibility"
                          onClick={handleTogglePasswordVisibility}
                          edge="end"
                        >
                          {showPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
                
                <TextField
                  margin="normal"
                  required
                  fullWidth
                  name="confirmPassword"
                  label="Confirmar contraseña"
                  type={showPassword ? 'text' : 'password'}
                  id="confirmPassword"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  disabled={loading}
                />
              </>
            ) : (
              // Paso 2: Datos personales
              <>
                <TextField
                  margin="normal"
                  required
                  fullWidth
                  id="name"
                  label="Nombre completo"
                  name="name"
                  autoComplete="name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  disabled={loading}
                />
                
                <TextField
                  margin="normal"
                  required
                  fullWidth
                  id="phone"
                  label="Teléfono"
                  name="phone"
                  autoComplete="tel"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  disabled={loading}
                />
              </>
            )}
            
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 3 }}>
              {activeStep > 0 && (
                <Button
                  onClick={handleBack}
                  disabled={loading}
                >
                  Atrás
                </Button>
              )}
              
              <Button
                type="submit"
                variant="contained"
                disabled={loading}
                sx={{ ml: 'auto' }}
              >
                {activeStep === steps.length - 1 ? 'Registrarse' : 'Siguiente'}
              </Button>
            </Box>
            
            <Grid container justifyContent="flex-end" sx={{ mt: 3 }}>
              <Grid item>
                <Link component={RouterLink} to="/login" variant="body2">
                  ¿Ya tienes una cuenta? Inicia sesión
                </Link>
              </Grid>
            </Grid>
          </Box>
        </Box>
      </Paper>
    </Container>
  );
};

export default RegisterPage;